import React, { useCallback, useEffect, useState } from "react";
import { Beaker, WifiOff } from "lucide-react";
import EmptyState from "../components/CommonComponents/EmptyState";
import SearchInput from "../components/CommonComponents/SearchInput";
import Pagination from "../components/CommonComponents/Pagination";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from "../utils/tableStyle";
import { fetchRawMaterialUsageLogs } from "../services/rawMaterialUsageServices";
import axiosInstance from "../services/axiosInstance";

const formatQty = (qty) => {
  const num = Number(qty);
  if (!Number.isFinite(num)) return "0";
  return Number.isInteger(num) ? String(num) : num.toFixed(3).replace(/\.?0+$/, "");
};

const RawMaterialLogsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [branches, setBranches] = useState([]);
  const [activeBranchTab, setActiveBranchTab] = useState("");

  const rowsPerPage = 10;
  const selectedBranchId = Number(activeBranchTab || "");

  const loadBranches = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/admin/branches");
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const active = data.filter((branch) => String(branch?.status || "ACTIVE") === "ACTIVE");
      setBranches(active);
      if (!active.length) {
        setActiveBranchTab("");
        return;
      }
      if (!activeBranchTab || !active.some((b) => String(b.id) === String(activeBranchTab))) {
        setActiveBranchTab(String(active[0].id));
      }
    } catch (err) {
      setBranches([]);
      setActiveBranchTab("");
    }
  }, [activeBranchTab]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchRawMaterialUsageLogs({
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm,
        startDate: appliedStartDate,
        endDate: appliedEndDate,
        branchId: selectedBranchId,
      });
      setRows(Array.isArray(response?.data) ? response.data : []);
      setTotalPages(Math.max(Number(response?.pagination?.totalPages) || 1, 1));
      setTotalCount(Number(response?.pagination?.total) || 0);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchTerm, appliedStartDate, appliedEndDate, selectedBranchId]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    if (!selectedBranchId) return;
    loadLogs();
  }, [loadLogs, selectedBranchId]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  const formatTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="pt-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ color: "var(--color-text)" }}>Raw Material Logs</h2>
        <p className="text-sm text-gray-500">Track raw material usage per branch.</p>
      </div>

      <div className="mb-4">
        <p className="text-sm font-semibold mb-2" style={{ color: "var(--color-text)" }}>Branches</p>
        <div className="flex gap-1 flex-wrap">
          {branches.map((branch) => (
            <button
              key={branch.id}
              type="button"
              className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${String(activeBranchTab) === String(branch.id)
                ? "bg-[var(--color-primary)] text-[var(--color-on-primary)] border-[var(--color-primary)]"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              onClick={() => {
                setActiveBranchTab(String(branch.id));
                setCurrentPage(1);
              }}
            >
              {branch.name}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Filter</h3>
        <div className="flex items-center gap-3 bg-[#f6f6f8] p-4 rounded-xl flex-wrap">
          <label className="text-sm font-semibold text-gray-700">Select Date Range:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate || undefined}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate || undefined}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
          />
          {(startDate && endDate) && (
            <button
              type="button"
              onClick={() => {
                setAppliedStartDate(startDate);
                setAppliedEndDate(endDate);
                setCurrentPage(1);
              }}
              className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Submit
            </button>
          )}
          {(appliedStartDate || appliedEndDate) && (
            <button
              type="button"
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setAppliedStartDate("");
                setAppliedEndDate("");
                setCurrentPage(1);
              }}
              className="text-sm font-semibold text-primary hover:text-primary-dark"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <SearchInput
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search logs by raw material..."
        />
      </div>

      {error ? (
        <EmptyState
          icon={WifiOff}
          title="Failed to Load Raw Material Logs"
          message="Check your connection and try again."
          onRetry={loadLogs}
          retryLabel="Reload"
        />
      ) : loading ? (
        <div className="text-center py-8 text-gray-500">Loading raw material logs...</div>
      ) : !rows.length ? (
        <EmptyState
          icon={Beaker}
          title="No Raw Material Logs"
          message="No usage records found for your current filter."
        />
      ) : (
        <>
          <div className={tableContainer}>
            <table className={tableBase}>
              <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                <tr>
                  <th className={tableHeader + " text-center"}>Date</th>
                  <th className={tableHeader + " text-center"}>Time</th>
                  <th className={tableHeader + " text-center"}>Branch</th>
                  <th className={tableHeader + " text-center"}>Raw Material</th>
                  <th className={tableHeader + " text-center"}>Qty Used</th>
                  <th className={tableHeader + " text-center"}>Unit</th>
                  <th className={tableHeader + " text-center"}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className={tableRow + " border-b last:border-0"}>
                    <td className={tableCell + " text-center"}>{formatDate(row.created_at || row.createdAt)}</td>
                    <td className={tableCell + " text-center"}>{formatTime(row.created_at || row.createdAt)}</td>
                    <td className={tableCell + " text-center"}>{row.branch?.name || "-"}</td>
                    <td className={tableCell + " text-center"}>{row.rawMaterial?.name || "-"}</td>
                    <td className={tableCell + " text-center tabular-nums"}>{formatQty(row.qty_used)}</td>
                    <td className={tableCell + " text-center"}>{row.rawMaterial?.unit_type || "-"}</td>
                    <td className={tableCell + " text-center"}>{row.reason || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col items-center gap-2 mt-3">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
            <p className="text-sm" style={{ color: "var(--color-text)", opacity: 0.7 }}>
              Showing {totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} -{" "}
              {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} items | Page {currentPage} of {totalPages}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default RawMaterialLogsPage;
